import PageHeader from "../components/ds/PageHeader";
import SegmentedControl from "../components/ds/SegmentedControl";
import Button from "../components/ds/Button";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import Menu, { MenuItem } from "../components/ds/Menu";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiLoader, FiTag } from "react-icons/fi";
import { expirePromoCode, getPromoCodes } from "../api/mintSpace";
import { useTranslation } from "../context/TranslationContext";
import ConfirmModal from "../components/ConfirmModal";
import UserProfileLink from "../components/UserProfileLink";
import CreatePromoCodePanel from "../components/promo/CreatePromoCodePanel";
import PromoCodeProductsPicker from "../components/promo/PromoCodeProductsPicker";
import { discountLabel } from "../utils/promoCodes";

/**
 * The owner's promo codes. Making one is two steps: the code and its rules, then — on
 * a popup of its own — the products it works on. It opens again from the list,
 * so a product created after the code can be added to it.
 *
 * @see mintly-back/src/services/promoCodeService
 */

const STATUS_TONE = {
  active: "bg-status-success-bg text-status-success",
  used_up: "bg-status-warning-bg text-status-warning",
  expired: "bg-slate-500/15 text-slate-400",
};
const FILTERS = ["active", "expired", "all"];

const dateTime = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime())
    ? d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
};
const day = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
};

export default function MintSpacePromoCodesPage({ isDark, setToast, mintSpaceId, spaceName }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ promoCodes: [], products: [], affiliates: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [creating, setCreating] = useState(false);
  /** { promo, isNew } while its products are being chosen. */
  const [picking, setPicking] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [busy, setBusy] = useState(false);
  /** The code whose row menu is open, and the button it opened from. */
  const [menu, setMenu] = useState(null);
  const menuAnchor = useRef(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  const load = useCallback(async () => {
    if (!mintSpaceId) return;
    try {
      const res = await getPromoCodes(mintSpaceId);
      setData(res?.data || { promoCodes: [], products: [], affiliates: [] });
    } catch (err) {
      setToast?.(err?.message || t("promoCodes.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [mintSpaceId, setToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const productNames = useMemo(
    () => new Map(data.products.map((p) => [p.id, p.name || t("purchases.untitled")])),
    [data.products, t]
  );

  const shown = useMemo(
    () =>
      data.promoCodes.filter((p) =>
        filter === "all" ? true : filter === "active" ? p.status === "active" : p.status !== "active"
      ),
    [data.promoCodes, filter]
  );

  /** Put a changed code back in the list without a reload. */
  const replace = (promo) =>
    setData((d) => ({
      ...d,
      promoCodes: d.promoCodes.some((p) => p.id === promo.id)
        ? d.promoCodes.map((p) => (p.id === promo.id ? promo : p))
        : [promo, ...d.promoCodes],
    }));

  const productsText = (promo) => {
    if (promo.appliesToAll) return t("promoCodes.allProducts");
    if (promo.products.length === 0) return null;
    if (promo.products.length === 1) return productNames.get(promo.products[0]);
    return t("promoCodes.productCount", { count: promo.products.length });
  };

  const limits = (promo) =>
    [
      promo.oncePerUser && t("promoCodes.oncePerUser"),
      promo.maxRedemptions > 0 && t("promoCodes.maxUses", { count: promo.maxRedemptions }),
      promo.expiresAt && t("promoCodes.until", { date: day(promo.expiresAt) }),
    ].filter(Boolean);

  const affiliateName = (a) => (a ? a.businessName || `@${a.username}` : "");

  const copy = async (promo) => {
    setMenu(null);
    try {
      await navigator.clipboard.writeText(promo.code);
      setToast?.(t("promoCodes.copied", { code: promo.code }));
    } catch {
      setToast?.(t("common.copyFailed"));
    }
  };

  const expire = async () => {
    if (!expiring) return;
    setBusy(true);
    try {
      const res = await expirePromoCode(mintSpaceId, expiring.id);
      replace(res.data);
      setToast?.(t("promoCodes.expiredToast", { code: expiring.code }));
      setExpiring(null);
    } catch (err) {
      setToast?.(err?.message || t("promoCodes.expireFailed"));
    } finally {
      setBusy(false);
    }
  };

  /** Everything in the current view, as a spreadsheet. */
  const exportCsv = () => {
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["code", "products", "discount", "limit", "uses", "affiliate", "status", "created"].map((c) =>
      t(`promoCodes.columns.${c}`)
    );
    const rows = shown.map((p) => [
      p.code,
      p.appliesToAll ? t("promoCodes.allProducts") : p.products.map((id) => productNames.get(id)).join("; "),
      discountLabel(p, t),
      limits(p).join("; "),
      p.uses,
      affiliateName(p.affiliate),
      t(`promoCodes.status.${p.status}`),
      dateTime(p.createdAt),
    ]);
    const csv = [header, ...rows].map((r) => r.map(cell).join(",")).join("\r\n");
    // The BOM lets Excel read Georgian text as UTF-8.
    const url = URL.createObjectURL(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "promo-codes.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const card = "border-separator-default bg-bg-raised";
  const hairline = "border-separator-default";
  const chip = isDark ? "bg-sky-500/15 text-sky-300" : "bg-sky-100 text-sky-700";
  const iconButton = `grid h-8 w-8 place-items-center rounded-radius-xs border transition ${
    "border-separator-default hover:bg-fill-quaternary"
  }`;

  // Opens the code's product page — the way to add products made since.
  const productsCell = (promo) => {
    const text = productsText(promo);
    return (
      <button
        type="button"
        onClick={() => setPicking({ promo, isNew: false })}
        title={t("promoCodes.chooseProducts")}
        className={`text-left underline decoration-dotted underline-offset-4 transition hover:text-accent-text ${
          text ? `${baseText} ${isDark ? "decoration-white/30" : "decoration-slate-300"}` : "font-semibold text-status-warning decoration-amber-500/50"
        }`}
      >
        {text || t("promoCodes.noProducts")}
      </button>
    );
  };

  const limitCell = (promo) => {
    const list = limits(promo);
    return list.length ? (
      <span className="flex flex-wrap gap-1">
        {list.map((l) => (
          <span key={l} className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-caption-1-semibold ${chip}`}>
            {l}
          </span>
        ))}
      </span>
    ) : (
      <span className={mutedText}>—</span>
    );
  };

  const affiliateCell = (promo) =>
    promo.affiliate ? (
      <UserProfileLink user={promo.affiliate} avatarSize="xs" isDark={isDark} nameClassName={`text-footnote ${baseText}`} />
    ) : (
      <span className={mutedText}>—</span>
    );

  const statusChip = (promo) => (
    <span className={`rounded-md px-2 py-0.5 text-caption-1-semibold ${STATUS_TONE[promo.status] || STATUS_TONE.expired}`}>
      {t(`promoCodes.status.${promo.status}`)}
    </span>
  );

  const actions = (promo) => (
    <div className="flex justify-end">
      <IconButton
        label={t("promoCodes.actions")}
        aria-haspopup="menu"
        aria-expanded={menu?.id === promo.id}
        onClick={(e) => {
          menuAnchor.current = e.currentTarget;
          setMenu(menu?.id === promo.id ? null : promo);
        }}
      >
        <Icon name="more" size={18} />
      </IconButton>
    </div>
  );

  const rowMenu = (
    <Menu open={!!menu} onClose={closeMenu} anchorRef={menuAnchor} label={t("promoCodes.actions")}>
      {menu && (
        <>
          <MenuItem icon="copy" onClick={() => copy(menu)}>
            {t("promoCodes.copy")}
          </MenuItem>
          <MenuItem icon="bag" onClick={() => setPicking({ promo: menu, isNew: false })}>
            {t("promoCodes.chooseProducts")}
          </MenuItem>
          {menu.status !== "expired" && (
            <MenuItem icon="trash" danger onClick={() => setExpiring(menu)}>
              {t("promoCodes.expire")}
            </MenuItem>
          )}
        </>
      )}
    </Menu>
  );

  return (
    <div className="flex w-full flex-col gap-space-24 p-space-8 lg:p-0">
      <PageHeader
        title={t("promoCodes.title")}
        subtitle={t("promoCodes.subtitle")}
        action={
          <Button size="m" icon={<Icon name="plus" size={20} />} onClick={() => setCreating(true)}>
            {t("promoCodes.create")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-space-8">
        <SegmentedControl
          track="material"
          label={t("promoCodes.title")}
          value={filter}
          onChange={setFilter}
          className="[&>button]:flex-auto [&>button]:px-space-8 min-[600px]:w-[360px]"
          options={FILTERS.map((f) => ({ value: f, label: t(`promoCodes.filter.${f}`) }))}
        />
        <Button size="s" variant="gray" icon={<Icon name="upload" size={16} />} disabled={shown.length === 0} onClick={exportCsv}>
          {t("promoCodes.export")}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
        </div>
      ) : shown.length === 0 ? (
        <div className={`flex flex-col items-center rounded-radius-md border border-dashed px-6 py-12 text-center border-separator-default`}>
          <FiTag className={`mb-3 h-7 w-7 ${mutedText}`} />
          <p className={`text-subheadline-semibold ${baseText}`}>
            {data.promoCodes.length === 0
              ? t("promoCodes.empty")
              : t(filter === "active" ? "promoCodes.noneActive" : "promoCodes.noneExpired")}
          </p>
          {data.promoCodes.length === 0 && <p className={`mt-1 text-subheadline ${mutedText}`}>{t("promoCodes.emptyHint")}</p>}
        </div>
      ) : (
        <>
          {/* Table — scrolls sideways where the space's sidebars leave it too little room */}
          <div className={`hidden overflow-x-auto rounded-radius-md border md:block ${card}`}>
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className={`border-b ${hairline}`}>
                  {/* "Created" goes under each code, which leaves the table room beside the sidebars. */}
                  {["code", "products", "discount", "limit", "uses", "affiliate", "status"].map((c) => (
                    <th key={c} className={`px-2.5 py-3 align-bottom text-caption-1 font-medium ${mutedText}`}>
                      {t(`promoCodes.columns.${c}`)}
                    </th>
                  ))}
                  <th className="px-2.5 py-3" />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} className={`border-b text-footnote last:border-b-0 ${hairline}`}>
                    <td className="px-2.5 py-3">
                      <p className={`font-mono font-bold ${baseText}`}>{p.code}</p>
                      <p className={`whitespace-nowrap text-caption-1 ${mutedText}`} title={dateTime(p.createdAt)}>
                        {t("promoCodes.createdOn", { date: day(p.createdAt) })}
                      </p>
                    </td>
                    <td className="max-w-[11rem] px-2.5 py-3">{productsCell(p)}</td>
                    <td className={`whitespace-nowrap px-2.5 py-3 ${baseText}`}>{discountLabel(p, t)}</td>
                    <td className="px-2.5 py-3">{limitCell(p)}</td>
                    <td className={`whitespace-nowrap px-2.5 py-3 ${baseText}`}>
                      {p.uses}
                      {p.maxRedemptions > 0 && <span className={mutedText}> / {p.maxRedemptions}</span>}
                    </td>
                    <td className="px-2.5 py-3">{affiliateCell(p)}</td>
                    <td className="px-2.5 py-3">{statusChip(p)}</td>
                    <td className="px-2.5 py-3">{actions(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards (phones) */}
          <ul className="space-y-3 md:hidden">
            {shown.map((p) => (
              <li key={p.id} className={`rounded-radius-md border p-4 ${card}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`font-mono text-subheadline-semibold ${baseText}`}>{p.code}</p>
                    <p className={`mt-0.5 text-subheadline ${baseText}`}>{discountLabel(p, t)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusChip(p)}
                    {actions(p)}
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 text-footnote">
                  <p className="flex gap-2">
                    <span className={`w-24 shrink-0 ${mutedText}`}>{t("promoCodes.columns.products")}</span>
                    {productsCell(p)}
                  </p>
                  <div className="flex gap-2">
                    <span className={`w-24 shrink-0 ${mutedText}`}>{t("promoCodes.columns.limit")}</span>
                    {limitCell(p)}
                  </div>
                  <p className="flex gap-2">
                    <span className={`w-24 shrink-0 ${mutedText}`}>{t("promoCodes.columns.uses")}</span>
                    <span className={baseText}>
                      {p.uses}
                      {p.maxRedemptions > 0 && <span className={mutedText}> / {p.maxRedemptions}</span>}
                    </span>
                  </p>
                  {p.affiliate && (
                    <div className="flex gap-2">
                      <span className={`w-24 shrink-0 ${mutedText}`}>{t("promoCodes.columns.affiliate")}</span>
                      {affiliateCell(p)}
                    </div>
                  )}
                  <p className={`pt-1 text-caption-1 ${mutedText}`}>{dateTime(p.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {rowMenu}

      {picking && (
        <PromoCodeProductsPicker
          isDark={isDark}
          mintSpaceId={mintSpaceId}
          spaceName={spaceName}
          promo={picking.promo}
          products={data.products}
          isNew={picking.isNew}
          onBack={() => setPicking(null)}
          onSaved={(promo) => {
            replace(promo);
            setPicking(null);
            setToast?.(t("promoCodes.products.saved", { code: promo.code }));
          }}
        />
      )}

      <CreatePromoCodePanel
        open={creating}
        isDark={isDark}
        mintSpaceId={mintSpaceId}
        affiliates={data.affiliates}
        onClose={() => setCreating(false)}
        onCreated={(promo) => {
          replace(promo);
          setCreating(false);
          setFilter("active");
          setToast?.(t("promoCodes.form.created", { code: promo.code }));
          // Step two: the products it works on, on a page of its own.
          setPicking({ promo, isNew: true });
        }}
      />

      <ConfirmModal
        isOpen={Boolean(expiring)}
        onClose={() => !busy && setExpiring(null)}
        onConfirm={expire}
        title={expiring ? t("promoCodes.expireTitle", { code: expiring.code }) : ""}
        message={t("promoCodes.expireMessage")}
        confirmLabel={t("promoCodes.expireConfirm")}
        variant="danger"
        loading={busy}
        isDark={isDark}
      />
    </div>
  );
}
